import React, { useState, useEffect, useCallback } from "react";
import { API_BASE_URL } from "../config";
import { useNavigate } from "react-router-dom";
import { Eye, Edit, Trash2, Package, ShoppingBag, Plus, AlertCircle } from "lucide-react";
import SellerOrders from "./SellerOrders";
import { PRODUCT_CATEGORIES } from "../constants/categories";

function ProductManager() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("products");
  const [state, setState] = useState({
    isVerifiedSeller: null,
    products: [],
    loading: true,
    error: null,
  });

  const token = localStorage.getItem("token");

  const fetchVerificationStatus = useCallback(async () => {
    if (!token) {
      setState((prev) => ({ ...prev, error: "Please login first", loading: false }));
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/check-verification`, {
        headers: { Authorization: token },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch verification status");
      }

      const data = await response.json();
      setState((prev) => ({ ...prev, isVerifiedSeller: data.isVerifiedSeller }));

      // ถ้ายังไม่ได้รับการอนุมัติ (Status 2 = อนุมัติแล้ว) ให้ไปหน้า seller-form
      if (data.isVerifiedSeller !== 2) {
        navigate("/account/seller-form");
      }
    } catch (error) {
      setState((prev) => ({ ...prev, error: error.message }));
    }
  }, [token, navigate]);

  const fetchProducts = useCallback(async () => {
    if (!token) {
      setState((prev) => ({ ...prev, error: "Please login first", loading: false }));
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/products/test`, {
        headers: { Authorization: token },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch products");
      }

      const data = await response.json();
      setState((prev) => ({ ...prev, products: data }));
    } catch (error) {
      setState((prev) => ({ ...prev, error: error.message }));
    }
  }, [token]);

  useEffect(() => {
    Promise.all([fetchVerificationStatus(), fetchProducts()]).finally(() =>
      setState((prev) => ({ ...prev, loading: false }))
    );
  }, [fetchVerificationStatus, fetchProducts]);

  const handleDelete = useCallback(
    async (productId) => {
      if (!token) {
        alert("Please login first");
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/api/products/${productId}`, {
          method: "DELETE",
          headers: { Authorization: token },
        });

        if (response.ok) {
          setState((prev) => ({
            ...prev,
            products: prev.products.filter((p) => p.productId !== productId),
          }));
        } else {
          const errorData = await response.json();
          alert(`Error: ${errorData.message || "Unknown error"}`);
        }
      } catch (error) {
        alert("Failed to connect to server");
      }
    },
    [token]
  );

  const getCategoryName = (id) => {
    const category = PRODUCT_CATEGORIES.find((c) => c.id === id);
    return category ? category.name : "Unknown";
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 1:
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Active</span>;
      case 2:
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">Out of Stock</span>;
      default:
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">Unknown</span>;
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-sm text-secondary">Loading...</div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-4">
        <div className="bg-red-50 p-4 rounded-md flex items-center max-w-md">
          <AlertCircle className="h-5 w-5 text-red-500 mr-3" />
          <p className="text-sm text-red-700">{state.error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-primary">Store Management</h1>
          <p className="text-sm text-secondary mt-1">Manage your products and orders.</p>
        </div>

        {activeTab === "products" && (
          <button
            onClick={() => navigate("add-product")}
            className="inline-flex items-center px-4 py-2 border border-black shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-black transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Product
          </button>
        )}
      </div>

      <div className="border-b border-gray-200 mb-8">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("products")}
            className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm flex items-center transition-colors ${activeTab === "products"
              ? "border-black text-primary"
              : "border-transparent text-secondary hover:text-primary hover:border-gray-300"
              }`}
          >
            <Package className="w-4 h-4 mr-2" />
            Products
          </button>
          <button
            onClick={() => setActiveTab("orders")}
            className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm flex items-center transition-colors ${activeTab === "orders"
              ? "border-black text-primary"
              : "border-transparent text-secondary hover:text-primary hover:border-gray-300"
              }`}
          >
            <ShoppingBag className="w-4 h-4 mr-2" />
            Incoming Orders
          </button>
        </nav>
      </div>

      {activeTab === "orders" ? (
        <SellerOrders />
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary uppercase tracking-wider">Status</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary uppercase tracking-wider">Product Name</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary uppercase tracking-wider">Category</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary uppercase tracking-wider">Type</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary uppercase tracking-wider">Qty</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary uppercase tracking-wider">Price</th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-secondary uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {state.products.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-sm text-secondary">No products found. Add your first product!</td>
                  </tr>
                ) : (
                  state.products.map((product) => (
                    <tr key={product.productId} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(product.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-primary">
                        {product.productName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary">
                        {getCategoryName(product.categoryId)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary">
                        {product.type === 1 ? "Single Item" : "Stock"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary">
                        {product.quantity}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-primary font-medium">
                        ฿{product.price.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => navigate(`view-product/${product.productId}`)}
                            className="text-gray-400 hover:text-primary transition-colors"
                            title="View"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            onClick={() => navigate(`edit-product/${product.productId}`)}
                            className="text-gray-400 hover:text-blue-600 transition-colors"
                            title="Edit"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm("Are you sure you want to delete this product?")) {
                                handleDelete(product.productId);
                              }
                            }}
                            className="text-gray-400 hover:text-red-600 transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductManager;
